import React, { useState } from 'react';
import { PauseCircle, Trash2, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { deleteAccount, deleteAccountWithOtp, requestDeleteOtp, resumeAccount } from '../api/account';

/** Locked screen shown when a user logs in with status='paused'. */
const PausedScreen = () => {
  const { user, refresh, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [pwd, setPwd] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const socialOnly = user?.has_password === false;

  const handleRequestOtp = async () => {
    setBusy(true);
    try {
      await requestDeleteOtp();
      setOtpSent(true);
      toast({ title: 'Code sent', description: `Check ${user?.email}` });
    } catch (e) {
      toast({ title: 'Could not send code', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleResume = async () => {
    setBusy(true);
    try {
      await resumeAccount();
      await refresh();
      toast({ title: 'Welcome back online' });
      navigate('/', { replace: true });
    } catch (e) {
      toast({ title: 'Could not resume', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (socialOnly) {
      if (!otp.trim()) return toast({ title: 'Enter the code we emailed you', variant: 'destructive' });
    } else if (!pwd) {
      return toast({ title: 'Enter your password', variant: 'destructive' });
    }
    setBusy(true);
    try {
      if (socialOnly) await deleteAccountWithOtp(otp.trim());
      else await deleteAccount(pwd);
      toast({ title: 'Account deleted' });
      logout();
      navigate('/login', { replace: true });
    } catch (e) {
      toast({
        title: socialOnly ? 'Invalid or expired code' : 'Wrong password',
        description: e.response?.data?.detail,
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6" data-testid="paused-screen">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/20">
          <PauseCircle className="h-7 w-7 gold-text" />
        </div>
        <h1 className="mb-1 text-2xl font-semibold">Your account is on standby</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Hi {user?.full_name || user?.username}, your profile and content are currently hidden
          from the public. Choose what you want to do next.
        </p>

        {!showDelete && (
          <div className="space-y-2">
            <Button
              onClick={handleResume}
              disabled={busy}
              className="h-11 w-full gold-gradient text-sm font-semibold"
              data-testid="resume-btn"
            >
              <Play className="mr-2 h-4 w-4" /> Come back online
            </Button>
            <Button
              onClick={() => setShowDelete(true)}
              variant="outline"
              className="h-11 w-full border-red-500/40 text-red-400 hover:bg-red-500/10"
              data-testid="show-delete-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Permanently delete account
            </Button>
          </div>
        )}

        {showDelete && (
          <div className="space-y-3 text-left">
            <p className="text-xs text-red-300">
              This will permanently erase your profile, posts and media (GDPR). It cannot be undone.
            </p>
            {socialOnly ? (
              <div className="flex gap-2" data-testid="paused-delete-otp-flow">
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit code from your email"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="bg-background"
                  disabled={!otpSent}
                  data-testid="paused-delete-otp"
                />
                <Button type="button" variant="outline" onClick={handleRequestOtp} disabled={busy} className="h-10 shrink-0" data-testid="paused-delete-request-otp-btn">
                  {otpSent ? 'Resend code' : 'Send code'}
                </Button>
              </div>
            ) : (
              <Input
                type="password"
                placeholder="Enter your current password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="bg-background"
                data-testid="paused-delete-pwd"
              />
            )}
            <div className="flex gap-2">
              <Button
                onClick={handleDelete}
                disabled={busy || (socialOnly && !otpSent)}
                className="h-10 flex-1 bg-red-600 text-white hover:bg-red-700"
                data-testid="paused-delete-btn"
              >
                Delete forever
              </Button>
              <Button onClick={() => setShowDelete(false)} variant="ghost" className="h-10 flex-1">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PausedScreen;
